/**
 * Registro de marcas (ADR-035): una entrada por dominio. La marca se fija en
 * build con `NEXT_PUBLIC_BRAND` (ver ./index.ts) y cada marca es un proyecto de
 * Vercel distinto sobre este mismo código.
 *
 * SIN imports a propósito: lo carga el middleware (edge) a través de
 * lib/locale-paths y lo lee lib/brand.ts desde cualquier capa. Las clases
 * Tailwind van como literales COMPLETOS para que el escáner de Tailwind v4
 * las genere (misma regla que lib/character-themes.ts).
 */

export type BrandId = 'santa' | 'raton-perez'

/** Familias clásicas de personaje (mismas claves que ClassicCharacterThemeKey). */
export type BrandFamily = 'santa' | 'reyes' | 'tooth-fairy' | 'raton-perez'

/** Traspaso SEO de una familia a otro dominio (solo en la marca primaria). */
export const HANDOVER_MODES = ['none', 'canonical', 'redirect'] as const
export type HandoverMode = (typeof HANDOVER_MODES)[number]

export interface BrandHandover {
  url: string
  mode: HandoverMode
}

export interface BrandTheme {
  /** `themeColor` del viewport y color del splash nativo. */
  themeColor: string
  /** Fondo del shell (legales, checkout, pago, loading, not-found). Clase completa. */
  shellGradient: string
  /** Fondo sólido del hero/video mock genérico. Clase completa. */
  heroBg: string
  /** El mismo color que `heroBg`, en crudo, para quien lo necesite fuera de Tailwind (OG). Lo ata un test. */
  heroHex: string
  /** Color del pie (dominio) de la imagen OG generada, legible sobre `themeColor → heroHex`. */
  ogAccent: string
  /**
   * Paleta de packs de la marca: clave de `PACK_STYLES` (lib/pack-styles.ts), la
   * misma tabla que usa `LandingPricing`. Solo la clave: este fichero no importa.
   * El pack destacado (el 6) es siempre ámbar en todas las paletas.
   */
  packVariant: 'santa' | 'raton-perez'
  /** Tarjeta de saldo del dashboard (superficie clara con variante dark): gradiente y color de borde, clase completa. */
  balanceCard: string
}

export interface BrandAssets {
  logo: string
  favicon: string
  /** Carpeta con favicon-16/32/48/64, icon-192/512 e ios-icon-120/152/180. */
  iconsDir: string
  manifest: string
  /** Manifest alternativo para ja (hoy solo santa). */
  manifestJa?: string
  homeOgImage: { url: string; width: number; height: number }
  /** Título que pinta la imagen OG de la home (`pnpm og:brand`): sin el sufijo de marca del `appShell`. */
  homeOgTitle: string
}

export interface Brand {
  id: BrandId
  /** Nombre visible: template de títulos, siteName, logo, JSON-LD. */
  name: string
  productionUrl: string
  supportEmail: string
  /** Valor de `utm_source` en los enlaces de email. Santa conserva el histórico `papanoelchat`. */
  utmSource: string
  /** Familias clásicas servidas por la marca (landings, guías, sitemap). */
  families: readonly BrandFamily[]
  /**
   * Familias que enseña el dashboard, EN EL ORDEN en que salen: `families` más las
   * que la marca ofrece solo dentro de la app, sin publicar sus landings en este
   * dominio (la canónica sigue en la marca que las sirve). Solo presentación y
   * orden: el servidor NO lo valida al abrir una llamada, así que no es un permiso.
   */
  dashboardFamilies: readonly BrandFamily[]
  /** Catálogo genérico (Mundo Mágico, Halloween, Conejo, Cumpleaños). */
  catalog: boolean
  /** Tracker «¿Dónde está Papá Noel?». */
  tracker: boolean
  /** Personaje propio (ADR-037): tarjeta del dashboard, API, llamada y landing `/tu-personaje`. */
  customCharacter: boolean
  /** Landing que ocupa `/`. Debe estar en `families`. */
  home: BrandFamily
  /** Slug de la puerta de acceso canónica ('auth' | 'auth-raton-perez' | …). */
  authSlug: string
  /** Solo la primaria sirve admin, ejecuta el cron y registra webhooks. */
  primary: boolean
  /** Título y descripción del árbol autenticado (`app/(app)/layout.tsx`). */
  appShell: { title: string; description: string }
  theme: BrandTheme
  assets: BrandAssets
  stores?: { appStoreId: string; playPackageId: string }
  /** Solo en la primaria: familias traspasadas a otro dominio (fase 8). */
  handover?: Partial<Record<BrandFamily, BrandHandover>>
}

export const DEFAULT_BRAND_ID: BrandId = 'santa'

export const BRANDS: Record<BrandId, Brand> = {
  santa: {
    id: 'santa',
    name: 'PapaNoel.chat',
    productionUrl: 'https://papanoel.chat',
    supportEmail: 'help@papanoel.chat',
    utmSource: 'papanoelchat',
    families: ['santa', 'reyes', 'tooth-fairy', 'raton-perez'],
    // Papá Noel primero (su card lleva el chat); después, el orden histórico del dashboard.
    dashboardFamilies: ['santa', 'tooth-fairy', 'raton-perez', 'reyes'],
    catalog: true,
    tracker: true,
    customCharacter: true,
    home: 'santa',
    authSlug: 'auth',
    primary: true,
    // Fase 8b (encendida 2026-09-14): las páginas del Ratón bajo papanoel.chat declaran
    // canonical hacia ratonperez.app y salen del sitemap. Fase 9: `mode: 'redirect'`
    // cuando Search Console confirme la indexación del dominio nuevo.
    handover: { 'raton-perez': { url: 'https://ratonperez.app', mode: 'canonical' } },
    appShell: {
      title: 'Videollamada personalizada con Santa Claus | PapaNoel.chat',
      description:
        'Videollamada personalizada con Santa Claus para tus hijos en tiempo real. Personaliza deseos, gustos y logros para una experiencia navideña única y mágica en familia.',
    },
    theme: {
      themeColor: '#8B0000',
      shellGradient: 'bg-gradient-to-b from-red-900 via-red-800 to-green-900',
      heroBg: 'bg-[#1a1a2e]',
      heroHex: '#1a1a2e',
      ogAccent: '#fde68a',
      packVariant: 'santa',
      balanceCard: 'bg-gradient-to-r from-red-50 to-green-50 dark:from-red-950/20 dark:to-green-950/20 border-red-200 dark:border-red-800',
    },
    assets: {
      logo: '/icons/icon-192.png',
      favicon: '/favicon.ico',
      iconsDir: '/icons',
      manifest: '/manifest.json',
      manifestJa: '/manifest-ja.json',
      homeOgImage: { url: '/santa-claus-video-call-waving-happy-in-his-worksho.jpg', width: 1200, height: 630 },
      homeOgTitle: 'Videollamada personalizada con Santa Claus',
    },
    stores: { appStoreId: '6758524861', playPackageId: 'com.papanoel.chat' },
  },
  'raton-perez': {
    id: 'raton-perez',
    name: 'RatonPerez.app',
    productionUrl: 'https://ratonperez.app',
    // Buzón compartido con la marca primaria (decisión 2026-09-14): `help@ratonperez.app`
    // no existe. Se abre un buzón propio solo si compartirlo llega a suponer fricción.
    supportEmail: 'help@papanoel.chat',
    utmSource: 'ratonperezapp',
    families: ['raton-perez'],
    // El Ratón primero; el Hada y Santa se llaman desde el dashboard, pero sus
    // landings siguen solo en papanoel.chat. Santa antes que el Hada: su card
    // (con el chat) ocupa dos columnas y, junto al Ratón, llena la primera fila de 3.
    dashboardFamilies: ['raton-perez', 'santa', 'tooth-fairy'],
    catalog: false,
    tracker: false,
    // Sin personaje propio: es un producto de papanoel.chat (su landing vive allí) y
    // ratonperez.app se queda con los personajes clásicos.
    customCharacter: false,
    home: 'raton-perez',
    authSlug: 'auth-raton-perez',
    primary: false,
    appShell: {
      title: 'Videollamada con el Ratón Pérez | RatonPerez.app',
      description:
        'Videollamada personalizada con el Ratón Pérez para celebrar cuando a tu hijo se le cae un diente. Conversación en tiempo real con su nombre y su historia.',
    },
    // Mismos valores que CHARACTER_THEMES['raton-perez'].landingGradient / heroBg
    // (copiados como literal: este fichero no puede importar). themeColor = amber-900.
    theme: {
      themeColor: '#78350f',
      shellGradient: 'bg-gradient-to-b from-amber-950 via-stone-950 to-amber-950',
      heroBg: 'bg-[#1a1510]',
      heroHex: '#1a1510',
      ogAccent: '#fcd34d',
      // Ámbar sobre piedra con destellos ✦ (el separador que ya usa su landing).
      packVariant: 'raton-perez',
      balanceCard: 'bg-gradient-to-r from-amber-50 to-stone-50 dark:from-amber-950/20 dark:to-stone-950/20 border-amber-200 dark:border-amber-800',
    },
    // Iconos, favicon y logo generados con `pnpm icons:brand --brand raton-perez`
    // (fase 7); OG de la home con `pnpm og:brand --brand raton-perez`.
    assets: {
      logo: '/brands/raton-perez/icons/icon-192.png',
      favicon: '/brands/raton-perez/icons/favicon.ico',
      iconsDir: '/brands/raton-perez/icons',
      manifest: '/brands/raton-perez/manifest.json',
      homeOgImage: { url: '/brands/raton-perez/og.jpg', width: 1200, height: 630 },
      homeOgTitle: 'Videollamada con el Ratón Pérez',
    },
  },
}

/** Una entrada de `handover` válida: url absoluta https sin barra final. */
function hasValidHandoverUrl(url: string): boolean {
  return url.startsWith('https://') && !url.endsWith('/')
}

function isValidHandoverMode(mode: string): boolean {
  return (HANDOVER_MODES as readonly string[]).includes(mode)
}

/** Otra marca del registro cuya `productionUrl` es exactamente la url de destino del traspaso. */
function destinationBrand(brands: Record<BrandId, Brand>, brand: Brand, url: string): Brand | undefined {
  return Object.values(brands).find((candidate) => candidate.id !== brand.id && candidate.productionUrl === url)
}

/**
 * Violaciones del destino: el canonical del traspaso apunta a `/` de la otra marca
 * (`hrefFor: getHomeHref`), así que esa marca debe servir la familia y tenerla en `home`.
 */
function validateHandoverDestination(brand: Brand, family: BrandFamily, destination: Brand): string[] {
  const violations: string[] = []
  if (!destination.families.includes(family)) {
    violations.push(`${brand.id}.${family}: la marca destino (${destination.id}) no sirve esa familia`)
  }
  if (destination.home !== family) {
    violations.push(
      `${brand.id}.${family}: la marca destino (${destination.id}) no tiene esa familia en home (el canonical apunta a /)`
    )
  }
  return violations
}

/** Violaciones de UNA entrada `family: handover` de una marca primaria. */
function validateHandoverEntry(
  brand: Brand,
  family: BrandFamily,
  entry: BrandHandover,
  brands: Record<BrandId, Brand>
): string[] {
  const violations: string[] = []
  const destination = destinationBrand(brands, brand, entry.url)
  if (!hasValidHandoverUrl(entry.url)) {
    violations.push(`${brand.id}.${family}: url debe ser absoluta https sin barra final (${entry.url})`)
  }
  if (!isValidHandoverMode(entry.mode)) {
    violations.push(`${brand.id}.${family}: mode inválido (${entry.mode})`)
  }
  if (!brand.families.includes(family)) {
    violations.push(`${brand.id}.${family}: la familia traspasada ya no está en families de la marca primaria`)
  }
  if (!destination) {
    violations.push(`${brand.id}.${family}: url no coincide con la productionUrl de ninguna otra marca del registro`)
  } else {
    violations.push(...validateHandoverDestination(brand, family, destination))
  }
  return violations
}

/**
 * Invariantes de un `handover` (fase 8b): solo la marca primaria puede declarar
 * traspasos; cada entrada lleva `url` absoluta `https://` sin barra final y
 * `mode` de `HandoverMode`; la familia traspasada sigue en `families` de la
 * primaria (si se quitara, los 308 de la fase 9 responderían 404); y `url`
 * coincide con la `productionUrl` de OTRA marca del registro (el destino es
 * una marca real, no un dominio externo), que además sirve esa familia y la
 * tiene en `home`. Devuelve la lista de violaciones; vacía = válido. Sin
 * imports a propósito (ver cabecera del fichero).
 *
 * No hay llamante en producción a propósito: el guardián es `tests/unit/lib/brands.test.ts`, que recorre `BRAND_IDS`
 * y falla en CI el día que la fase 8b añada una entrada inválida.
 */
export function validateHandover(brand: Brand, brands: Record<BrandId, Brand>): string[] {
  if (!brand.handover) return []
  if (!brand.primary) return [`${brand.id}: solo la marca primaria puede declarar handover`]

  const violations: string[] = []
  for (const [family, entry] of Object.entries(brand.handover) as [BrandFamily, BrandHandover | undefined][]) {
    if (entry) violations.push(...validateHandoverEntry(brand, family, entry, brands))
  }
  return violations
}

export const BRAND_IDS = Object.keys(BRANDS) as BrandId[]

export function isBrandId(value: unknown): value is BrandId {
  return typeof value === 'string' && (BRAND_IDS as readonly string[]).includes(value)
}

/** Marca por id persistido (p. ej. `Parent.signupBrand`); null/desconocido → marca por defecto. */
export function brandById(id: string | null | undefined): Brand {
  if (isBrandId(id)) return BRANDS[id]
  if (id != null) console.warn(`[brands] signupBrand desconocido "${id}": se usa la marca por defecto`)
  return BRANDS[DEFAULT_BRAND_ID]
}
